import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, Row, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from './useStudyYearSelector';

const todayISO = () => new Date().toISOString().slice(0, 10);

const statusBadge = (status) => {
  const map = {
    pending: ['warning', 'รออนุมัติ'],
    approved: ['success', 'อนุมัติแล้ว'],
    rejected: ['danger', 'ปฏิเสธ'],
    cancelled: ['secondary', 'ยกเลิก']
  };
  const [variant, label] = map[status] || ['secondary', status];
  return <Badge bg={variant}>{label}</Badge>;
};

export default function DisciplineHomeLeave() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();
  const [keyword, setKeyword] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState('all');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    leaveDate: todayISO(),
    returnDate: todayISO(),
    reason: '',
    guardianName: '',
    guardianPhone: '',
    status: 'pending',
    note: ''
  });

  useEffect(() => { setSelectedStudent(null); }, [yearId]);

  const searchStudents = useCallback((e) => {
    e?.preventDefault();
    if (!yearId) return;
    axiosInstance.get(`/api/discipline/conduct/students?yearId=${yearId}&keyword=${encodeURIComponent(keyword.trim())}`)
      .then(res => setStudents(res.data || []))
      .catch(() => setError('ไม่สามารถค้นหานักเรียนได้'));
  }, [yearId, keyword]);

  const fetchRequests = useCallback(() => {
    if (!yearId) return;
    const params = new URLSearchParams({ yearId, status });
    if (keyword.trim()) params.set('keyword', keyword.trim());
    axiosInstance.get(`/api/discipline/home-leave?${params.toString()}`)
      .then(res => setRequests(res.data || []))
      .catch(() => setError('ไม่สามารถโหลดคำขอกลับบ้านได้'));
  }, [yearId, status, keyword]);

  useEffect(() => {
    if (yearId) {
      searchStudents();
      fetchRequests();
    }
  }, [yearId, status, searchStudents, fetchRequests]);

  const createRequest = (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      setError('กรุณาเลือกนักเรียน');
      return;
    }
    setError('');
    setMessage('');
    axiosInstance.post('/api/discipline/home-leave', {
      studentId: selectedStudent.st_id,
      studyId: selectedStudent.study_id,
      yearId,
      ...form
    }).then(res => {
      setMessage(res.data?.quota_warning
        ? 'บันทึกแล้ว แต่เดือนนี้นักเรียนเคยมีรายการกลับบ้านที่อนุมัติแล้ว'
        : 'บันทึกคำขอกลับบ้านแล้ว');
      setForm({ leaveDate: todayISO(), returnDate: todayISO(), reason: '', guardianName: '', guardianPhone: '', status: 'pending', note: '' });
      fetchRequests();
    }).catch(err => setError(err.response?.data?.message || 'ไม่สามารถบันทึกได้'));
  };

  const updateStatus = (leaveId, nextStatus) => {
    axiosInstance.put('/api/discipline/home-leave', { leaveId, status: nextStatus })
      .then(() => {
        setMessage('อัปเดตสถานะแล้ว');
        fetchRequests();
      })
      .catch(() => setError('ไม่สามารถอัปเดตสถานะได้'));
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">อนุญาตกลับบ้าน</li>
            </ol>
            <h4 className="main-title mb-0">คำขออนุญาตกลับบ้าน</h4>
          </div>
          <Dropdown className="mt-3 mt-md-0">
            <Dropdown.Toggle variant="primary" id="discipline-home-leave-year">
              {yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(year => (
                <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                  {`ปีการศึกษา ${year.year_year}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {message && <div className={`alert ${message.includes('เคยมี') ? 'alert-warning' : 'alert-success'}`}>{message}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="g-3">
          <Col xl={4}>
            <Card className="card-one mb-3">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">เลือกนักเรียน</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={searchStudents}>
                  <InputGroup>
                    <Form.Control value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="เลขประจำตัว หรือชื่อ" />
                    <Button type="submit">ค้นหา</Button>
                  </InputGroup>
                </Form>
                <div className="mt-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
                  <Table hover size="sm" className="mb-0">
                    <tbody>
                      {students.map(student => (
                        <tr
                          key={student.st_id}
                          role="button"
                          className={selectedStudent?.st_id === student.st_id ? 'table-primary' : ''}
                          onClick={() => setSelectedStudent(student)}
                        >
                          <td>
                            <div className="fw-semibold">{student.st_name}</div>
                            <div className="fs-xs text-secondary">{student.st_idnumber} · ม.{student.c_id}/{student.r_no}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">บันทึกคำขอ</Card.Title>
              </Card.Header>
              <Card.Body>
                {selectedStudent && (
                  <div className="alert alert-primary py-2">
                    {selectedStudent.st_name}<br />
                    <span className="fs-xs">ม.{selectedStudent.c_id}/{selectedStudent.r_no} · {selectedStudent.st_idnumber}</span>
                  </div>
                )}
                <Form onSubmit={createRequest}>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Label>วันที่ออก</Form.Label>
                      <Form.Control type="date" value={form.leaveDate} onChange={e => setForm({ ...form, leaveDate: e.target.value })} required />
                    </Col>
                    <Col md={6}>
                      <Form.Label>วันที่กลับ</Form.Label>
                      <Form.Control type="date" value={form.returnDate} onChange={e => setForm({ ...form, returnDate: e.target.value })} required />
                    </Col>
                    <Col xs={12}>
                      <Form.Label>เหตุผล</Form.Label>
                      <Form.Control as="textarea" rows={2} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required />
                    </Col>
                    <Col md={6}>
                      <Form.Label>ผู้รับ/ผู้ปกครอง</Form.Label>
                      <Form.Control value={form.guardianName} onChange={e => setForm({ ...form, guardianName: e.target.value })} />
                    </Col>
                    <Col md={6}>
                      <Form.Label>เบอร์ติดต่อ</Form.Label>
                      <Form.Control value={form.guardianPhone} onChange={e => setForm({ ...form, guardianPhone: e.target.value })} />
                    </Col>
                    <Col md={6}>
                      <Form.Label>สถานะเริ่มต้น</Form.Label>
                      <Form.Select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                        <option value="pending">รออนุมัติ</option>
                        <option value="approved">อนุมัติทันที</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label>หมายเหตุ</Form.Label>
                      <Form.Control value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
                    </Col>
                    <Col xs={12}>
                      <Button type="submit">บันทึกคำขอ</Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={8}>
            <Card className="card-one">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <Card.Title as="h6" className="mb-0">รายการคำขอ</Card.Title>
                <Form.Select size="sm" className="ms-auto" style={{ width: 170 }} value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="all">ทุกสถานะ</option>
                  <option value="pending">รออนุมัติ</option>
                  <option value="approved">อนุมัติแล้ว</option>
                  <option value="rejected">ปฏิเสธ</option>
                  <option value="cancelled">ยกเลิก</option>
                </Form.Select>
                <Button variant="outline-secondary" size="sm" onClick={fetchRequests}>
                  <i className="ri-refresh-line"></i>
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th>นักเรียน</th>
                      <th>วันที่ออก-กลับ</th>
                      <th>เหตุผล</th>
                      <th className="text-center">สถานะ</th>
                      <th className="text-center" style={{ width: 170 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(req => (
                      <tr key={req.leave_id}>
                        <td>
                          <div className="fw-semibold">{req.st_name}</div>
                          <div className="fs-xs text-secondary">{req.st_idnumber} · ม.{req.c_id}/{req.r_no}</div>
                        </td>
                        <td>
                          {new Date(req.leave_date).toLocaleDateString('th-TH')} - {new Date(req.return_date).toLocaleDateString('th-TH')}
                          <div className="fs-xs text-secondary">{req.guardian_name || '-'} {req.guardian_phone || ''}</div>
                        </td>
                        <td>{req.reason}</td>
                        <td className="text-center">{statusBadge(req.status)}</td>
                        <td className="text-center">
                          {req.status === 'pending' ? (
                            <div className="btn-group btn-group-sm">
                              <Button variant="outline-success" onClick={() => updateStatus(req.leave_id, 'approved')}>อนุมัติ</Button>
                              <Button variant="outline-danger" onClick={() => updateStatus(req.leave_id, 'rejected')}>ปฏิเสธ</Button>
                            </div>
                          ) : (
                            <Button variant="outline-secondary" size="sm" onClick={() => updateStatus(req.leave_id, 'cancelled')}>ยกเลิก</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {requests.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-secondary py-5">ยังไม่มีรายการคำขอ</td></tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  );
}
